function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { assertNotNull } from '@deephaven/utils';
import Papa from 'papaparse';
// Intentionally using isNaN rather than Number.isNaN
/* eslint-disable no-restricted-globals */
import { NewTableColumnTypes } from '@deephaven/jsapi-utils';
import makeZipStreamHelper from "./ZipStreamHelper.js"; // Initially column types start as unknown
var UNKNOWN = 'unknown';
var MAX_INT = 2147483647;
var MIN_INT = -2147483648;
var DATE_TIME_REGEX = /^[0-9]{4}-[0-1][0-9]-[0-3][0-9][ T][0-2][0-9]:[0-5][0-9]:[0-6][0-9](?:\.[0-9]{1,9})?(?: [a-zA-Z]+)?$/;
var LOCAL_TIME_REGEX = /^([0-9]+T)?([0-9]+):([0-9]+)(:[0-9]+)?(?:\.[0-9]{1,9})?$/;

/**
 * Determines the type of each column in a CSV file by parsing it and looking at every value.
 */
class CsvTypeParser {
  static determineType(value, type, nullString) {
    if (!value || value === nullString) {
      // A null tells us nothing about the type
      return type;
    }
    switch (type) {
      case NewTableColumnTypes.STRING:
        // Strings never get promoted
        return NewTableColumnTypes.STRING;
      case NewTableColumnTypes.INTEGER:
        return CsvTypeParser.checkInteger(value);
      case NewTableColumnTypes.LONG:
        return CsvTypeParser.checkLong(value);
      case NewTableColumnTypes.DOUBLE:
        return CsvTypeParser.checkDouble(value);
      case NewTableColumnTypes.BOOLEAN:
        return CsvTypeParser.checkBoolean(value);
      case NewTableColumnTypes.DATE_TIME:
        return CsvTypeParser.checkDateTime(value);
      case NewTableColumnTypes.LOCAL_TIME:
        return CsvTypeParser.checkLocalTime(value);
      default:
        return CsvTypeParser.getTypeFromUnknown(value);
    }
  }

  // Allows for cusomt rules in addition to isNaN
  static isNotParsableNumber(s) {
    return isNaN(s) || s === 'Infinity' || s === '-Infinity';
  }
  static checkInteger(value) {
    var noCommas = value.replace(/,/g, '');
    if (CsvTypeParser.isNotParsableNumber(noCommas)) {
      return NewTableColumnTypes.STRING;
    }
    return CsvTypeParser.getNumberType(noCommas);
  }
  static checkLong(value) {
    var noCommas = value.replace(/,/g, '');
    if (CsvTypeParser.isNotParsableNumber(noCommas)) {
      return NewTableColumnTypes.STRING;
    }
    if (noCommas.includes('.')) {
      return NewTableColumnTypes.DOUBLE;
    }
    return NewTableColumnTypes.LONG;
  }
  static checkDouble(value) {
    var noCommas = value.replace(/,/g, '');
    if (CsvTypeParser.isNotParsableNumber(noCommas)) {
      return NewTableColumnTypes.STRING;
    }
    return NewTableColumnTypes.DOUBLE;
  }
  static checkBoolean(value) {
    var lower = value.toLowerCase();
    if (lower === 'true' || lower === 'false') {
      return NewTableColumnTypes.BOOLEAN;
    }
    return NewTableColumnTypes.STRING;
  }
  static checkDateTime(value) {
    if (DATE_TIME_REGEX.test(value)) {
      return NewTableColumnTypes.DATE_TIME;
    }
    return NewTableColumnTypes.STRING;
  }
  static checkLocalTime(value) {
    if (LOCAL_TIME_REGEX.test(value)) {
      return NewTableColumnTypes.LOCAL_TIME;
    }
    return NewTableColumnTypes.STRING;
  }
  static getTypeFromUnknown(value) {
    var noCommas = value.replace(/,/g, '');
    if (CsvTypeParser.isNotParsableNumber(noCommas)) {
      var lower = value.toLowerCase();
      if (lower === 'true' || lower === 'false') {
        return NewTableColumnTypes.BOOLEAN;
      }
      if (DATE_TIME_REGEX.test(value) && value.includes(':')) {
        return NewTableColumnTypes.DATE_TIME;
      }
      if (LOCAL_TIME_REGEX.test(value)) {
        return NewTableColumnTypes.LOCAL_TIME;
      }
      return NewTableColumnTypes.STRING;
    }
    return CsvTypeParser.getNumberType(noCommas);
  }
  static getNumberType(value) {
    if (value.includes('.')) {
      return NewTableColumnTypes.DOUBLE;
    }

    // Fast length check
    var maxIntLength = value.startsWith('-') ? 11 : 10;
    if (value.length > maxIntLength) {
      return NewTableColumnTypes.LONG;
    }

    // Slower parseInt check
    var intValue = parseInt(value, 10);
    if (intValue > MAX_INT || intValue < MIN_INT) {
      return NewTableColumnTypes.LONG;
    }
    return NewTableColumnTypes.INTEGER;
  }
  constructor(onFileCompleted, file, readHeaders, parentConfig, nullString, onProgress, onError, totalChunks, isZip, shouldTrim) {
    _defineProperty(this, "onFileCompleted", void 0);
    _defineProperty(this, "file", void 0);
    _defineProperty(this, "readHeaders", void 0);
    _defineProperty(this, "nullString", void 0);
    _defineProperty(this, "onProgress", void 0);
    _defineProperty(this, "onError", void 0);
    _defineProperty(this, "types", void 0);
    _defineProperty(this, "chunks", void 0);
    _defineProperty(this, "totalChunks", void 0);
    _defineProperty(this, "rowCount", void 0);
    _defineProperty(this, "isZip", void 0);
    _defineProperty(this, "shouldTrim", void 0);
    _defineProperty(this, "zipProgress", void 0);
    _defineProperty(this, "config", void 0);
    this.onFileCompleted = onFileCompleted;
    this.file = file;
    this.readHeaders = readHeaders;
    this.nullString = nullString;
    this.onProgress = onProgress;
    this.onError = onError;
    this.chunks = 0;
    this.totalChunks = totalChunks;
    this.rowCount = 0;
    this.isZip = isZip;
    this.shouldTrim = shouldTrim;
    this.zipProgress = 0;
    this.handleChunk = this.handleChunk.bind(this);
    this.handleComplete = this.handleComplete.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleNodeUpdate = this.handleNodeUpdate.bind(this);
    this.config = _objectSpread(_objectSpread({}, parentConfig), {}, {
      error: this.handleError,
      chunk: this.handleChunk,
      complete: this.handleComplete
    });
  }
  parse() {
    if (this.file instanceof File || this.file instanceof Blob) {
      Papa.parse(this.file, this.config);
    } else {
      var zipStream = makeZipStreamHelper(this.file, this.handleNodeUpdate);
      // This is actually a stream, but papaparse TS doesn't like it
      Papa.parse(zipStream, this.config);
      // The stream needs to be manually resumed since jszip starts paused
      // Papaparse does not call resume and assumes the stream is already reading
      zipStream.resume();
    }
  }
  handleChunk(result, parser) {
    var data = result.data;
    if (!this.types) {
      if (data == null || data.length === 0) {
        parser.abort();
        this.onError('Error parsing CSV: no data in file.');
        return;
      }
      this.types = new Array(data[0].length).fill(UNKNOWN);
      if (this.readHeaders) {
        // If headers are being read from the file, remove them from type analysis
        data = data.slice(1);
      }
    }
    this.rowCount += data.length;
    assertNotNull(this.types);
    var cloneTypes = [...this.types];
    data.forEach(row => {
      if (row.length >= cloneTypes.length) {
        for (var i = 0; i < cloneTypes.length; i += 1) {
          cloneTypes[i] = CsvTypeParser.determineType(this.shouldTrim ? row[i].trim() : row[i], cloneTypes[i], this.nullString);
        }
        this.types = cloneTypes;
      } else {
        parser.abort();
        this.onError("Error parsing CSV: Insufficient data in row.\nExpected length ".concat(cloneTypes.length, " but found ").concat(row.length, ".\nRow = ").concat(row));
      }
    });
    this.chunks += 1;
    // 50 because the type parsing accounts for 50% of the parsing
    var progress = 0;
    if (this.totalChunks > 0) {
      progress = Math.round(this.chunks / this.totalChunks * 50);
    } else {
      progress = Math.round(this.zipProgress / 2);
    }
    var isCancelled = this.onProgress(progress);
    if (isCancelled) {
      parser.abort();
    }
  }
  handleComplete(results) {
    var types = this.types,
      onFileCompleted = this.onFileCompleted;
    // results is undefined for a succesful parse, but has meta data for an abort
    assertNotNull(types);
    if (results == null || !results.meta.aborted) {
      onFileCompleted(types.map(type =>
      // If the type is still unknown or a local time, just map it to a string.
      // Local times are not supported by the backend in DHC, and probably should have more context to parse safely anyway (such as a date or a time zone).
      // In these cases, we just map it to a string, and the user can use an `.update_view` later if they want to parse it into a different type.
      type === UNKNOWN || type === NewTableColumnTypes.LOCAL_TIME ? NewTableColumnTypes.STRING : type), this.rowCount);
    }
  }
  handleError(error) {
    var onError = this.onError;
    onError(error);
  }
  handleNodeUpdate(metadata) {
    this.zipProgress = metadata.percent;
  }
}
export default CsvTypeParser;
//# sourceMappingURL=CsvTypeParser.js.map